<template>
  <div>
    <!-- Search and Status -->
    <v-row align="center">
      <v-col cols="12" xl="3" lg="3" md="3" sm="4" xs="12">
        <SearchText
          :text="'Search by title'"
          :value="'title'"
          @searchTextChanged="searchTextChanged"
          @keypress.enter.native.prevent="triggerSearch"
          @triggerSearch="triggerSearch"
          @triggerClear="triggerClear"
        ></SearchText>
      </v-col>
      <v-col class="d-flex align-center justify-end">
        <v-btn @click="resetStatus" class="font-weight-black mx-5" medium plain>
          Reset
        </v-btn>
        <v-select
          v-model="status"
          :items="itemsStatus"
          dense
          style="max-width: 245px"
          outlined
          label="Status"
          rounded
          hide-details
          return-object
          @input="changeStatus"
        >
        </v-select>
      </v-col>
    </v-row>
    <v-row v-if="orders.length === 0">
      <v-col cols="12" class="text-center">
        There are not any orders currently.
      </v-col>
    </v-row>

    <!-- Main Body -->
    <v-row class="mt-10">
      <v-col cols="12" v-for="(order, index) in orders" :key="'order-' + index">
        <v-card style="border-radius: 24px;" elevation="3">
          <v-card-text style="background: var(--v-primary50-base) !important;">
            <v-row>
              <v-col cols="auto" class="d-flex align-center">
                <div>
                  <v-chip flat label small color="blue" class="white--text font-weight-bold">
                    Patent:
                  </v-chip>
                </div>
                <label class="ml-5">
                  {{ order.patent.name }}
                </label>
              </v-col>
              <v-col cols="auto" class="d-flex align-center">
                <div>
                  <v-chip flat label small color="blue" class="white--text font-weight-bold">
                    Organisation:
                  </v-chip>
                </div>
                <label class="ml-5">
                  <router-link
                    :to="{
                      name: 'OverviewProvider',
                      params: { id: order.patent.organisation.id },
                      query: { patentOrdersQuery: true },
                    }"
                    style="text-decoration: underline; color: inherit;"
                  >
                    {{ order.patent.organisation.name }}
                  </router-link>
                </label>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="auto" class="d-flex align-center justify-end">
                <div>
                  Estimated Between:
                </div>
                <label class="ml-2">
                  {{ formatDate(order.fromDate) }} - {{ formatDate(order.toDate) }}
                </label>
              </v-col>
            </v-row>
            <v-row v-if="order.status.id === 'COMPLETED_BY_PROVIDER'">
              <v-col cols="auto" class="d-flex">
                <div class="d-flex align-center">
                  Your Rating:
                </div>
                <div>
                  <v-rating
                    v-if="order.uuidEvaluation"
                    @input="triggerEvaluationDialog(order)"
                    dense
                    hover
                    class="custom-rating"
                  ></v-rating>
                  <v-rating
                    v-else
                    v-model="order.orderPatentEvaluation.stars"
                    readonly
                    dense
                    hover
                    class="custom-rating"
                  ></v-rating>
                </div>
              </v-col>
              <v-col
                cols="auto"
                class="d-flex"
                v-if="order.orderPatentEvaluation && order.orderPatentEvaluation.feedback"
              >
                <v-btn small plain outlined @click="toggleFeedback(order)">
                  Your Feedback
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-text>
            <v-row>
              <v-col cols="3">
                <label>Requested</label>
                <div>{{ formatDateTime(order.createdTimestamp) }}</div>
              </v-col>
              <v-col cols="3">
                <label>Provider Review</label>
                <div>{{ formatDateTime(order.updatedByProvider) }}</div>
              </v-col>
              <v-col cols="3" align="end" class="text-center">
                <label>Researcher Review</label>
                <div>{{ formatDateTime(order.updatedByResearcher) }}</div>
              </v-col>
              <v-col cols="3" align="end" v-if="order.status.id === 'INVALIDATED_BY_PROVIDER'">
                <label style="color: red; font-weight: bold;">
                  Invalidated
                </label>
                <div>{{ formatDateTime(order.completedTimestamp) }}</div>
              </v-col>
              <v-col cols="3" align="end" v-else>
                <label
                  :style="
                    order.status.id === 'COMPLETED_BY_PROVIDER'
                      ? { color: 'green', fontWeight: 'bold' }
                      : {}
                  "
                >
                  Completed
                </label>
                <div>
                  {{ formatDateTime(order.completedTimestamp) }}
                </div>
              </v-col>
            </v-row>
            <div style="position: relative;">
              <v-progress-linear
                :value="getProgress(order)"
                color="green"
                id="equipment-progress-bar"
                height="25"
                rounded
                class="mb-4 mt-4"
              >
              </v-progress-linear>
              <v-icon
                v-if="
                  order.status.id === 'REJECTED_BY_PROVIDER' ||
                    order.status.id === 'REJECTED_BY_RESEARCHER' ||
                    order.status.id === 'INVALIDATED_BY_PROVIDER'
                "
                small
                :style="{
                  position: 'absolute',
                  left: `${getProgress(order) - 0.5}%`,
                  transform: 'translateX(-50%)',
                  top: '-1px',
                  color: 'white !important',
                }"
              >
                mdi-close-circle
              </v-icon>
              <v-icon
                v-if="
                  order.status.id === 'ACCEPTED_BY_PROVIDER' ||
                    order.status.id === 'ACCEPTED_BY_RESEARCHER' ||
                    order.status.id === 'COMPLETED_BY_PROVIDER'
                "
                small
                :style="{
                  position: 'absolute',
                  left: `${getProgress(order) - 0.5}%`,
                  transform: 'translateX(-50%)',
                  top: '-1px',
                  color: 'black !important',
                }"
              >
                mdi-check-circle
              </v-icon>
            </div>
            <v-row>
              <v-spacer></v-spacer>
              <v-col align="start" cols="6" v-if="order.status.id === 'REJECTED_BY_PROVIDER'">
                <div style="color: red; font-weight: bold;">Rejected</div>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="5" v-if="order.status.id === 'REJECTED_BY_RESEARCHER'">
                <div style="color: red; font-weight: bold;">Rejected</div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-text>
            <v-row>
              <v-col cols="auto">
                <div>
                  User:
                </div>
                <label>
                  {{ order.user.fullName }}
                </label>
              </v-col>
              <v-col class="mx-10">
                <div>
                  Order Date:
                </div>
                <label>
                  {{ formatDateTime(order.createdTimestamp) || '-' }}
                </label>
              </v-col>
              <v-col class="d-flex justify-end">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      @click="overviewOrder(order)"
                      v-bind="attrs"
                      v-on="on"
                      small
                      fab
                      class="mr-2"
                      color="white"
                    >
                      <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                        mdi-eye-outline
                      </v-icon>
                    </v-btn>
                  </template>
                  Overview
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-show="order.status.id === 'ACCEPTED_BY_PROVIDER'"
                      @click="acceptOrder(order)"
                      v-bind="attrs"
                      v-on="on"
                      small
                      fab
                      class="mr-2"
                      color="white"
                    >
                      <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                        mdi-handshake-outline
                      </v-icon>
                    </v-btn>
                  </template>
                  Accept
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-show="order.status.id === 'ACCEPTED_BY_PROVIDER'"
                      @click="rejectOrder(order)"
                      v-bind="attrs"
                      v-on="on"
                      small
                      fab
                      class="mr-2"
                      color="white"
                    >
                      <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                        mdi-cancel
                      </v-icon>
                    </v-btn>
                  </template>
                  Reject
                </v-tooltip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- OverView Order Dialog -->
    <v-dialog v-model="overviewOrderDialog" max-width="1000px" persistent :retain-focus="false">
      <v-card class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title class="pb-5">
          <v-row>
            <v-col cols="12" class="d-flex pa-0 text-center justify-center">
              <p class="font-weight-bold neutral--text pb-4 pt-6">{{ 'View Order' }}</p>
            </v-col>
          </v-row>
          <v-btn icon @click="overviewOrderDialog = false" left>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6" class="pt-5">
              <label>Patent</label>
              <div>
                {{ selectedOrder?.patent?.name || '-' }}
              </div>
            </v-col>
            <v-col cols="6" class="pt-5">
              <label>Provider</label>
              <div>
                {{ selectedOrder?.patent?.organisation?.name || '-' }}
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="pt-2">
              <label>Order By:</label>
              <div>
                {{ selectedOrder?.user?.fullName || '-' }}
              </div>
            </v-col>
            <v-col cols="6" class="pt-2">
              <label>Order Date:</label>
              <div>
                {{ formatDateTime(selectedOrder?.createdTimestamp) || '-' }}
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="pt-2">
              <label>From:</label>
              <div>{{ formatDate(selectedOrder?.fromDate) }}</div>
            </v-col>
            <v-col cols="6" class="pt-2">
              <label>To:</label>
              <div>{{ formatDate(selectedOrder?.toDate) }}</div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="pt-2">
              <label>Use Case:</label>
              <div>{{ selectedOrder?.useCase }}</div>
            </v-col>
            <v-col cols="6" class="pt-2">
              <label>Additional Prerequisites:</label>
              <div>{{ selectedOrder?.useCase }}</div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="pt-2">
              <label>Status:</label>
              <div>
                {{ selectedOrder?.status?.description || '-' }}
              </div>
            </v-col>
            <v-col cols="6" class="pt-2">
              <label>Cost:</label>
              <div>
                {{ selectedOrder?.cost || '-' }}
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="pt-2">
              <label>Public Contact Email:</label>
              <div>
                {{ selectedOrder?.publicContactEmail || '-' }}
              </div>
            </v-col>
            <v-col cols="6" class="pt-2">
              <label>Email:</label>
              <div>
                {{ selectedOrder?.email || '-' }}
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="pt-2">
              <label>Instructions:</label>
              <div>
                {{ selectedOrder?.instructions || '-' }}
              </div>
            </v-col>
            <v-col cols="6" class="pt-2">
              <label>Phone:</label>
              <div>
                {{ selectedOrder?.phone || '-' }}
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col v-if="selectedOrder && selectedOrder.comments != null" cols="12" class="pt-2">
              <label>Accepted by Provider Comments:</label>
              <div>
                {{ selectedOrder?.comments || '-' }}
              </div>
            </v-col>
            <v-col
              v-if="selectedOrder && selectedOrder.acceptResearcherCommends != null"
              cols="12"
              class="pt-2"
            >
              <label>Accepted by Researcher Comments:</label>
              <div>
                {{ selectedOrder?.acceptResearcherCommends || '-' }}
              </div>
            </v-col>
            <v-col
              v-if="selectedOrder && selectedOrder.rejectionProviderReason != null"
              cols="6"
              class="pt-2"
            >
              <label>Rejected by Provider Reason:</label>
              <div>
                {{ selectedOrder?.rejectionProviderReason?.description || '-' }}
              </div>
            </v-col>
            <v-col
              v-if="selectedOrder && selectedOrder.rejectComments != null"
              cols="6"
              class="pt-2"
            >
              <label>Rejected by Provider Comments:</label>
              <div>
                {{ selectedOrder?.rejectComments || '-' }}
              </div>
            </v-col>
            <v-col
              v-if="selectedOrder && selectedOrder.rejectionResearcherReason != null"
              cols="6"
              class="pt-2"
            >
              <label>Rejected by Researcher Reason:</label>
              <div>
                {{ selectedOrder?.rejectionResearcherReason?.description || '-' }}
              </div>
            </v-col>
            <v-col
              v-if="selectedOrder && selectedOrder.rejectResearcherCommends != null"
              cols="6"
              class="pt-2"
            >
              <label>Rejected by Researcher Comments:</label>
              <div>
                {{ selectedOrder?.rejectResearcherCommends || '-' }}
              </div>
            </v-col>
            <v-col
              v-if="selectedOrder && selectedOrder.completeCommends != null"
              cols="12"
              class="pt-2"
            >
              <label>Completed by Provider Comments:</label>
              <div style="color: green; font-weight: bold">
                {{ selectedOrder?.completeCommends || '-' }}
              </div>
            </v-col>
            <v-col
              v-if="selectedOrder && selectedOrder.invalidateCommends != null"
              cols="12"
              class="pt-2"
            >
              <label>Invalidated by Provider Comments:</label>
              <div style="color: red; font-weight: bold">
                {{ selectedOrder?.invalidateCommends || '-' }}
              </div>
            </v-col>
            <v-col
              v-if="selectedOrder && selectedOrder.forceCompleteCommends != null"
              cols="12"
              class="pt-2"
            >
              <label>Completed by Admin Comments:</label>
              <div style="color: green; font-weight: bold">
                {{ selectedOrder?.forceCompleteCommends || '-' }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- FeedBack Dialog -->
    <v-dialog v-model="feedbackDialog" max-width="600px" persistent :retain-focus="false">
      <v-card class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title class="pb-5">
          <v-row class="d-flex justify-center text-center">
            <img
              alt="Logo"
              width="180"
              class="rounded-0 text-center"
              :src="require('@/assets/svg/logo.svg')"
            />
          </v-row>
          <v-btn icon @click="feedbackDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-col cols="12" class="d-flex pa-0 text-center justify-center">
            <p class="font-weight-bold neutral--text pb-4 pt-6">{{ 'Evaluation Feedback' }}</p>
          </v-col>
        </v-card-text>
        <v-card-text>
          <v-form ref="form" v-model="valid" :lazy-validation="false">
            <v-row>
              <v-col cols="12">
                <label>Feedback</label>
                <v-textarea
                  :value="selectedOrder?.orderPatentEvaluation?.feedback || '-'"
                  class="mt-2"
                  :placeholder="'Feedback'"
                  readonly
                  outlined
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Reject Order Dialog -->
    <v-dialog v-model="rejectOrderDialog" max-width="1000px" persistent>
      <v-card class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title class="pb-5">
          <v-row>
            <v-col cols="12" class="d-flex pa-0 text-center justify-center">
              <p class="font-weight-bold neutral--text pb-4 pt-6">{{ 'Reject Order' }}</p>
            </v-col>
          </v-row>
          <v-btn icon @click="rejectOrderDialog = false" left>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form ref="rejectOrderForm" v-model="valid" :lazy-validation="false">
            <v-row>
              <v-col cols="12" class="py-0">
                <label>Rejection Reason</label>
                <v-select
                  v-model.trim="order.rejectionReason"
                  :items="rejectionReasons"
                  item-text="text"
                  item-value="value"
                  :placeholder="'Select a reason'"
                  outlined
                  clearable
                  :rules="requiredRule()"
                  class="required"
                  :menu-props="{ bottom: true, offsetY: true }"
                ></v-select>
              </v-col>
              <v-col v-if="order.rejectionReason == 'OTHER'" cols="12" class="py-0">
                <label>Comment</label>
                <v-textarea
                  v-model.trim="order.rejectComments"
                  :placeholder="'Comments'"
                  outlined
                  clearable
                  :rules="[
                    ...requiredRule(),
                    v => !v || v.length <= 250 || 'Maximum 250 characters allowed',
                  ]"
                  class="required"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-row style="width: 100%">
            <v-col cols="12">
              <v-btn
                @click="rejectOrderByResearcher(selectedOrder)"
                :disabled="!valid"
                block
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="black"
                >{{ $t('submit') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Accept Order Dialog -->
    <v-dialog v-model="acceptOrderDialog" max-width="1000px" persistent>
      <v-card class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title class="pb-5">
          <v-row>
            <v-col cols="12" class="d-flex pa-0 text-center justify-center">
              <p class="font-weight-bold neutral--text pb-4 pt-6">{{ 'Accept Order' }}</p>
            </v-col>
          </v-row>
          <v-btn icon @click="acceptOrderDialog = false" left>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form ref="acceptOrderForm" v-model="valid" :lazy-validation="false">
            <v-row>
              <v-col cols="12" class="py-0">
                <label>Comments</label>
                <v-textarea
                  v-model="order.comments"
                  :placeholder="'Comments'"
                  outlined
                  clearable
                  :rules="requiredRule()"
                  class="required"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-row style="width: 100%">
            <v-col cols="12">
              <v-btn
                @click="acceptOrderByResearcher(selectedOrder)"
                :disabled="!valid"
                block
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="black"
                >{{ $t('submit') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Evaluation Dialog -->
    <v-dialog v-model="evaluationDialog" max-width="600px" persistent :retain-focus="false">
      <v-card class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title class="pb-5">
          <v-row class="d-flex justify-center text-center">
            <img
              alt="Logo"
              width="180"
              class="rounded-0 text-center"
              :src="require('@/assets/svg/logo.svg')"
            />
          </v-row>
          <v-btn icon @click="evaluationDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-col cols="12" class="d-flex pa-0 text-center justify-center">
            <p class="font-weight-bold neutral--text pb-4 pt-6">{{ 'Evaluate Patent' }}</p>
          </v-col>
        </v-card-text>
        <v-card-text>
          <v-form ref="form" v-model="valid" :lazy-validation="false">
            <v-row>
              <v-col cols="12">
                <label>Rating</label>
                <v-rating v-model="evaluation.stars" dense hover class="custom-rating"></v-rating>
              </v-col>
              <v-col cols="12">
                <label>Feedback</label>
                <v-textarea
                  class="mt-2"
                  v-model="evaluation.feedback"
                  :placeholder="'Feedback'"
                  outlined
                  clearable
                  :rules="maxLengthRule(255)"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn
            :disabled="!valid || evaluation.stars == null"
            @click="submitEvaluation(selectedOrder)"
            block
            depressed
            rounded
            large
            class="white--text font-weight-bold"
            color="black"
            >{{ $t('submit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Pagination -->
    <v-row v-if="pagesLength > 1">
      <v-spacer></v-spacer>
      <v-col cols="4" md="2" class="justify-end d-flex flex-row align-center">
        <div>{{ totalItems }} Results</div>
      </v-col>
      <v-col cols="8" md="2" class="justify-end d-flex flex-row align-center">
        <div class="pr-2">Show:</div>
        <div style="max-width: 90px">
          <v-select
            v-model="options.itemsPerPage"
            :items="[10, 20, 30, 40, 50, 100]"
            dense
            outlined
            hide-details
            @change="onItemsPerPageChange"
          ></v-select>
        </div>
      </v-col>
      <v-col cols="12" md="6" class="justify-end d-flex flex-row align-center">
        <v-pagination
          v-model="options.page"
          :length="pagesLength"
          :total-visible="7"
          @input="onPageChange"
        ></v-pagination>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import SearchText from '@/components/shared/search/SearchText';
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';

export default {
  name: 'MyOrdersPatentResearcher',
  components: { SearchText },
  mixins: [util],
  props: {
    search: Object,
    searchAttributes: Array,
  },
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      order: {
        cost: '',
        instructions: '',
        phone: '',
        email: '',
        comments: '',
        rejectComments: '',
        rejectionReason: null,
      },
      rejectionReasons: [
        { text: 'Request Created By Mistake', value: 'REQUEST_CREATED_BY_MISTAKE' },
        { text: 'No Longer Needed', value: 'NO_LONGER_NEEDED' },
        { text: 'Found Alternative Solution', value: 'FOUND_ALTERNATIVE_SOLUTION' },
        { text: 'Budget Constraints', value: 'BUDGET_CONSTRAINTS' },
        { text: 'Waiting Time Too Long', value: 'WAITING_TIME_TOO_LONG' },
        { text: 'Decided To Postpone Request', value: 'DECIDED_TO_POSTPONE_REQUEST' },
        { text: 'Other', value: 'OTHER' },
      ],
      overviewOrderDialog: false,
      feedbackDialog: false,
      rejectOrderDialog: false,
      acceptOrderDialog: false,
      selectedOrder: null,
      valid: false,
      status: null,
      orders: [],
      totalItems: 0,
      pagesLength: null,
      progress: null,
      evaluationDialog: false,
      evaluation: {
        stars: null,
        feedback: null,
      },
      itemsStatus: [
        { text: 'Requested', value: 'REQUESTED', desc: true },
        { text: 'Completed', value: 'COMPLETED_BY_PROVIDER', desc: true },
        { text: 'Invalidated', value: 'INVALIDATED_BY_PROVIDER', desc: true },
        { text: 'Accepted by Provider', value: 'ACCEPTED_BY_PROVIDER', desc: true },
        { text: 'Rejected by Provider', value: 'REJECTED_BY_PROVIDER', desc: true },
        { text: 'Accepted by Researcher', value: 'ACCEPTED_BY_RESEARCHER', desc: true },
        { text: 'Rejected by Researcher', value: 'REJECTED_BY_RESEARCHER', desc: true },
      ],
    };
  },
  mounted() {
    this.getOrders();
    this.order.email = localStorage.getItem('email') || '';

    if (this.searchAttributes) {
      for (let i = 0; i < this.searchAttributes.length; i += 1) {
        const attr = this.searchAttributes[i];
        if (this.$route.query[attr]) {
          this.$set(this.search, attr, this.$route.query[attr]);
        }
      }
    }
  },
  methods: {
    searchTextChanged(obj) {
      console.log('[Table] searchTextChanged');
      this.search[obj.value] = obj.search;
      console.log(this.search);
    },
    triggerSearch() {
      console.log('[Table] Search watch');
      this.options.page = 1;
      this.getOrders();
    },
    triggerClear() {
      console.log('[Table] Clear watch');

      this.search = {};

      this.options.page = 1;
      this.getOrders();
    },
    changeStatus() {
      this.options.page = 1;
      this.getOrders();
    },
    resetStatus() {
      this.status = null;
      this.options.page = 1;
      this.getOrders();
    },
    getOrders() {
      console.log('[Table] Loading data');
      this.loading = true;
      EventBus.$emit('waiting', true);

      this.axios
        .get(`api/v1/orders-patent`, {
          params: this.getParams(),
        })
        .then(res => {
          if (res.status === 200) {
            console.log('Orders fetched successfully:', res.data);
            this.orders = res.data.content;
            this.totalItems = res.data.total;
            this.pagesLength = res.data.pagesLength;
          }
        })
        .catch(error => {
          console.error('Error', error.res);
        })
        .finally(() => {
          this.loading = false;
          EventBus.$emit('waiting', false);
        });
    },
    onItemsPerPageChange() {
      this.options.page = 1;
      this.getOrders();
    },
    onPageChange(newPage) {
      this.options.page = newPage;
      this.getOrders();
    },
    getParams() {
      const { page, itemsPerPage } = this.options;

      const query = {
        index: itemsPerPage === -1 ? 0 : page - 1,
        size: itemsPerPage === -1 ? 1000 : itemsPerPage,
      };

      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.search[attr]) {
            query[attr] = this.search[attr];
          }
        }
      }

      if (this.status && this.status.value) {
        query.status = this.status.value;
      }

      return query;
    },
    getProgress(order) {
      switch (order.status.id) {
        case 'REQUESTED':
          return 0;
        case 'ACCEPTED_BY_PROVIDER':
          return 33;
        case 'REJECTED_BY_PROVIDER':
          return 33;
        case 'ACCEPTED_BY_RESEARCHER':
          return 66;
        case 'REJECTED_BY_RESEARCHER':
          return 66;
        case 'COMPLETED':
          return 100;
        case 'COMPLETED_BY_PROVIDER':
          return 100;
        case 'INVALIDATED_BY_PROVIDER':
          return 100;
      }
    },
    overviewOrder(order) {
      this.selectedOrder = { ...order };
      this.overviewOrderDialog = true;
    },
    triggerEvaluationDialog(order) {
      this.selectedOrder = { ...order };
      this.evaluationDialog = true;
    },
    toggleFeedback(order) {
      this.selectedOrder = { ...order };
      this.feedbackDialog = true;
    },
    rejectOrder(order) {
      this.selectedOrder = { ...order };
      this.rejectOrderDialog = true;
    },
    acceptOrder(order) {
      this.selectedOrder = { ...order };
      this.acceptOrderDialog = true;
    },
    acceptOrderByResearcher(order) {
      const uuid = order.uuidResearcher;
      const payload = {
        uuid: uuid,
        commends: this.order.comments,
      };

      this.axios
        .put(`/api/v1/orders-patent/accept-by-researcher`, payload)
        .then(response => {
          console.log('Order accepted successfully:', response.data);
          this.acceptOrderDialog = false;
          EventBus.$emit('snackbar', {
            text: 'Order accepted successfully',
          });
          this.order.comments = '';
          this.$refs.acceptOrderForm.resetValidation();
          this.getOrders().then(res => {
            if (res.status === 200) {
              this.orders = res.data.content;
              this.totalItems = res.data.total;
              this.pagesLength = res.data.pagesLength;
            }
          });
        })
        .catch(error => {
          console.error('Failed to accept order:', error);
        });
    },
    rejectOrderByResearcher(order) {
      const uuid = order.uuidResearcher;
      const payload = {
        commends: this.order.rejectComments || null,
        rejectionResearcherReason: this.order.rejectionReason,
        uuid: uuid,
      };

      this.axios
        .put(`/api/v1/orders-patent/reject-by-researcher`, payload)
        .then(response => {
          console.log('Order rejected successfully:', response.data);
          this.rejectOrderDialog = false;
          EventBus.$emit('snackbar', {
            text: 'Order rejected successfully',
          });
          this.order.rejectComments = '';
          this.order.rejectionReason = null;
          this.$refs.rejectOrderForm.resetValidation();
          this.getOrders().then(res => {
            if (res.status === 200) {
              this.orders = res.data.content;
              this.totalItems = res.data.total;
              this.pagesLength = res.data.pagesLength;
            }
          });
        })
        .catch(error => {
          console.error('Failed to reject order:', error);
        });
    },
    submitEvaluation(selectedOrder) {
      const uuid = selectedOrder.uuidEvaluation;

      EventBus.$emit('waiting', true);

      this.axios
        .post('/api/v1/orders-patent/evaluation-by-researcher?uuid=' + uuid, this.evaluation)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: 'Evaluation successfully submitted',
          });
          this.evaluationDialog = false;
          this.evaluation.stars = null;
          this.evaluation.feedback = null;
          this.getOrders().then(data => {
            this.orders = data.content;
            this.totalItems = data.total;
            this.pagesLength = data.pagesLength;
          });
        })
        .catch(error => {
          console.log(error);
        })
        .finally(EventBus.$emit('waiting', false));
    },
  },
};
</script>

<style scoped>
.custom-rating {
  color: #e9ae1ae0;
}
</style>
